// transcript-anchor — the seam between a cited summary and a transcript that
// happens to be rendered on the same page.
//
// `<CitedSummary>` renders `[N]` citation markers wherever an ATLAS summary is
// shown (Calls & Meetings, Client Knowledge, Comply > Call Intelligence, the
// KB-notes panel, the review drawer). Only Calls & Meetings also renders the
// call's transcript, so only there can a marker scroll the reader onto the
// cited line. Rather than every consumer threading a prop through, the page
// that owns a transcript publishes this anchor around it; `CitedSummary` reads
// it if present and otherwise falls back to a deep link to that page.
//
// Provided by the expanded call row in `pages/desk/calls-meetings/page.tsx`.

import { createContext, useContext } from 'react';

export interface TranscriptAnchor {
    /** True when the rendered transcript contains this line id. */
    hasLine: (lineId: string) => boolean;
    /**
     * Scroll the first reachable line of a citation into view inside the
     * transcript's own scroll container and briefly highlight every cited line.
     */
    focusLines: (lineIds: readonly string[]) => void;
}

const TranscriptAnchorContext = createContext<TranscriptAnchor | null>(null);

/** Wrap the region that may contain cited summaries alongside the transcript. */
export const TranscriptAnchorProvider = TranscriptAnchorContext.Provider;

/** The anchor for the surrounding page, or null when no transcript is rendered. */
export function useTranscriptAnchor(): TranscriptAnchor | null {
    return useContext(TranscriptAnchorContext);
}

/** DOM id of a rendered transcript line — the scroll target of a `[N]` marker. */
export function transcriptLineDomId(lineId: string): string {
    return `transcript-line-${lineId}`;
}
